import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { full_name: "", company: "", email: "", phone: "", monthly_volume: "", service_area: "", service_type: "", additional_info: "" };

export function VolumePricingDialog() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.full_name.trim() || !f.company.trim() || !f.email.trim() || !f.phone.trim() || !f.monthly_volume.trim()) {
      toast.error("Please fill in all required fields."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.post("/volume-pricing", f);
      setDone(true);
    } catch (e) {
      toast.error("Could not submit your request. Please try again.");
    } finally { setBusy(false); }
  };

  const onOpenChange = (o) => { setOpen(o); if (!o) { setDone(false); setF(EMPTY); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button data-testid="request-volume-pricing-btn" className="rounded-full border border-slate-400 px-6 py-3 text-sm font-semibold text-white transition hover:border-white hover:bg-white/10">
          Request Volume Pricing
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="volume-pricing-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="volume-pricing-success">
            <DialogHeader><DialogTitle>Request received</DialogTitle></DialogHeader>
            <p className="mt-3 text-sm text-slate-600">Thank you. We've received your request and will contact you shortly regarding volume pricing and service options.</p>
            <Button className="mt-6" onClick={() => onOpenChange(false)} data-testid="volume-pricing-close">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader><DialogTitle>High-Volume Client — Request Volume Pricing</DialogTitle></DialogHeader>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Full Name *</Label><Input data-testid="vp-full-name" value={f.full_name} onChange={set("full_name")} /></div>
              <div className="sm:col-span-2"><Label>Company / Law Firm Name *</Label><Input data-testid="vp-company" value={f.company} onChange={set("company")} /></div>
              <div><Label>Email *</Label><Input type="email" data-testid="vp-email" value={f.email} onChange={set("email")} /></div>
              <div><Label>Phone *</Label><Input data-testid="vp-phone" value={f.phone} onChange={set("phone")} /></div>
              <div className="sm:col-span-2"><Label>Approximate Monthly Service Volume *</Label><Input data-testid="vp-volume" value={f.monthly_volume} onChange={set("monthly_volume")} placeholder="e.g., 50–100 services / month" /></div>
              <div className="sm:col-span-2"><Label>Primary Service Area / Counties</Label><Input data-testid="vp-area" value={f.service_area} onChange={set("service_area")} /></div>
              <div className="sm:col-span-2"><Label>Type of Service Needed</Label><Input data-testid="vp-type" value={f.service_type} onChange={set("service_type")} /></div>
              <div className="sm:col-span-2"><Label>Additional Information</Label><Textarea data-testid="vp-info" value={f.additional_info} onChange={set("additional_info")} rows={3} /></div>
            </div>
            <DialogFooter className="mt-4">
              <Button data-testid="vp-submit" onClick={submit} disabled={busy} className="bg-sky-600 text-white hover:bg-sky-700">{busy ? "Submitting…" : "Submit Request"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
